import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { AccountService } from './account.service';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class InactivityService {
  private lastActivityTime: number;
  private checkInterval: any;
  private readonly INACTIVITY_LIMIT =5 * 60 * 5000; 
  private readonly WARNING_LIMIT = 0.5 * 60 * 5000; 
  private isWarningShown = false;
  private isLoggedIn = false;

  constructor(
    private apiService: ApiService,
    private accService: AccountService,
    private router: Router,
    private ngZone: NgZone,
    private translate: TranslateService
  ) {
    this.accService.user.subscribe(user => {
      if (user) {
        this.isLoggedIn = true;
        this.startMonitoring();
      } else {
        this.isLoggedIn = false;
        this.stopMonitoring();
      }
    });
  }

  private startMonitoring() {
    this.lastActivityTime = Date.now();
    this.isWarningShown = false;
    
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('mousemove', this.resetTimer);
      window.addEventListener('keydown', this.resetTimer);
      window.addEventListener('click', this.resetTimer);
      window.addEventListener('scroll', this.resetTimer);
      
      this.checkInterval = setInterval(() => {
        this.checkInactivity();
      }, 1000);
    });
  }

  private stopMonitoring() {
    this.ngZone.runOutsideAngular(() => {
      window.removeEventListener('mousemove', this.resetTimer);
      window.removeEventListener('keydown', this.resetTimer);
      window.removeEventListener('click', this.resetTimer);
      window.removeEventListener('scroll', this.resetTimer);
      
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
      }
    });
  }

  private resetTimer = () => {
    this.lastActivityTime = Date.now();
    if (this.isWarningShown) {
      this.ngZone.run(() => {
        Swal.close();
        this.isWarningShown = false;
      });
    }
  }

  private checkInactivity() {
    if (!this.isLoggedIn) return;

    const now = Date.now();
    const diff = now - this.lastActivityTime;

    if (diff > this.INACTIVITY_LIMIT) {
      this.ngZone.run(() => this.logout());
    } else if (diff > this.WARNING_LIMIT && !this.isWarningShown) {
      this.ngZone.run(() => this.showWarning());
    }
  }

  private showWarning() {
    this.isWarningShown = true;
    let timerInterval;
    
    Swal.fire({
      title: this.translate.instant('inactivity-warning-title') || 'Inactivity Warning',
      html: (this.translate.instant('inactivity-warning-text') || 'You will be logged out in') + ' <b></b> ' + (this.translate.instant('seconds') || 'seconds.'),
      timer: 30000, 
      timerProgressBar: true,
      showCancelButton: true,
      confirmButtonText: this.translate.instant('stay-logged-in') || 'Stay Logged In',
      cancelButtonText: this.translate.instant('logout') || 'Logout',
      allowOutsideClick: false,
      didOpen: () => {
        const b = Swal.getHtmlContainer().querySelector('b');
        timerInterval = setInterval(() => {
          if (b) {
             const timeLeft = Math.ceil(Swal.getTimerLeft() / 1000);
             b.textContent = timeLeft.toString();
          }
        }, 100);
      },
      willClose: () => {
        clearInterval(timerInterval);
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.resetTimer();
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        this.logout();
      }
    });
  }

  private logout() {
    this.stopMonitoring();
    const username = sessionStorage.getItem("userName");
    
    if (Swal.isVisible()) {
      Swal.close();
    }

    this.apiService.logOut(username).subscribe(
      res => {
        this.accService.logout();
        this.router.navigate(["/login"]);
      },
      err => {
        this.accService.logout();
        this.router.navigate(["/login"]);
      }
    );
  }
}
